import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import DestructorTree from "../DestructorTree";
import { registrationInitialized } from "../state/registerClientState";
import { Failed } from "../Status";
import { WeakCache, createVariable, ReadonlyVariable } from "../../../../../stream";
import { toStream } from "../toStream";
import { setModified } from "../state/SaveModifiedStatus";
import { setStickyStateGroupValue } from "../state/mapCommandParameters";
import { addStateAttributes } from "../state/addClientState";
import { is_updating, stop_updating, notifyEntityUpdate } from "../widgets/instructions/update";
import { nodeEditMode } from "../state/editMode";
import { setStateLocationToLocal, setNodeLocationToRemote } from "../state/setLocation";
import { ordered_graphs, iGraph } from "../state/Graph";
import { Sink } from "../Symbols";
import { iNode } from "../state/Node";
import ClientStateObjectProxy from "./ClientStateObjectProxy";

type Stategroup = api_cs.Cstate_group__type__property;
type State = api_cs.Cstates__state_group__type__property;

function getSelectedState(stategroup: Stategroup): State | undefined {
	switch (stategroup.properties.is_set.state.name) {
		case "yes": {
			const $ = stategroup.properties.is_set.state.node;
			return $.properties.state.ref;
		}
		case "no":
			return undefined;
		default:
			unexpectedState(stategroup.properties.is_set.state);
	}
}

/**@deprecated*/
export async function subscribeToSelectedState<R>(
	stategroup: Stategroup,
	destructor: DestructorTree,
	callback: (state: State | undefined, destructor: DestructorTree) => R
): Promise<R> {
	await registrationInitialized(stategroup);
	let selected_state: State | undefined | null = null;
	let child_destructor = destructor.addChild();
	function on_selected(new_selected_state: State | undefined) {
		if (selected_state !== null && selected_state !== new_selected_state) {
			child_destructor.destroy();
			child_destructor = destructor.addChild();
			callback(new_selected_state, child_destructor);
		}
		selected_state = new_selected_state;
	}
	destructor.subscribeToStategroup(stategroup.properties.is_set, (destructor) => {
		switch (stategroup.properties.is_set.state.name) {
			case "yes": {
				const $ = stategroup.properties.is_set.state.node;
				return destructor.subscribeToText($, "subscribe_state", () => on_selected($.properties.state.ref));
			}
			case "no":
				return on_selected(undefined);
			default:
				unexpectedState(stategroup.properties.is_set.state);
		}
	});
	return callback(getSelectedState(stategroup), child_destructor);
}

class StategroupInterface extends ClientStateObjectProxy {
	readonly selected_state: ReadonlyVariable<State | Failed>;

	constructor(readonly stategroup: Stategroup) {
		super();
		this.selected_state = createVariable(
			toStream(stategroup.properties.is_set)
				.stateSwitch({
					no: () => Failed as State | Failed,
					yes: (is_set) => toStream(is_set, "state").pipe(() => is_set.properties.state.ref)(),
				})
				.pipe((v) => v)(),
			false
		);
	}

	getSelectedState(): State | undefined {
		const is_set = this.stategroup.properties.is_set.state;
		switch (is_set.name) {
			case "yes":
				return is_set.node.properties.state.ref;
			case "no":
				return undefined;
		}
	}

	updatePermissions(permitted: "yes" | "no") {
		switch (this.stategroup.inferences.state_group_type().properties.type.state.name) {
			case "base":
				switch (permitted) {
					case "no":
						this.stategroup.properties.is_editable.ensure("no", {});
						break;
					case "yes":
						this.stategroup.properties.is_editable.ensure("yes", {
							"edit mode": nodeEditMode(this.stategroup.parent.parent.parent),
						});
						break;
				}
				break;
			case "derived":
		}
	}

	discard() {
		this.stategroup.properties.initialized.switch({
			yes: ($) => iState($.properties.state_in_backend.ref).select("engine"),
			no: () => {
				this.stategroup.properties.is_editable.switch({
					yes: () => {
						this.stategroup.properties.is_modified.ensure("no", {});
						this.deselect();
					},
					no: undefined,
				});
			},
		});
		for (const state of this.stategroup.properties.states.values()) {
			iNode(state.properties.node).discard();
		}
		notifyEntityUpdate(this.stategroup.parent);
	}

	deselect() {
		this.stategroup.properties.is_set.switch({
			no: () => undefined,
			yes: ($) => {
				const state = $.properties.state.ref;
				state.parent.properties.is_set.set("no", {});
				state.properties.is_selected.set("no", {});
				iNode(state.properties.node).discard();
			},
		});
	}
}

export type iStategroup = StategroupInterface;
export const iStategroup = WeakCache<Stategroup, StategroupInterface>((sg) => new StategroupInterface(sg));

class StateInterface extends ClientStateObjectProxy {
	readonly stategroup: Stategroup;
	readonly is_selected: ReadonlyVariable<api_cs.Cnode | Failed>;
	graph: iGraph | undefined;
	entry: api_cs.Centries__collection | undefined;

	constructor(readonly state: State) {
		super();
		this.stategroup = state.parent;
		this.is_selected = createVariable(
			toStream(state.properties.is_selected)
				.stateSwitch({
					no: () => Failed as api_cs.Cnode | Failed,
					yes: () => state.properties.node,
				})
				.pipe((v) => v)(),
			false
		);
		this.graph = state.component_root.input.entity().switch({
			root: () => undefined,
			entry: (entry) => {
				this.entry = entry;
				for (const graph of ordered_graphs(entry.parent)) {
					if (state.key.ref === graph.sink_state) {
						return graph;
					}
				}
				return undefined;
			},
		});
	}

	__select(by: "user" | "engine") {
		this.state.properties.is_selected.switch({
			yes: () => undefined, // Already set
			no: () => {
				if (is_updating(this.stategroup.parent)) {
					return;
				}
				const state_name = this.state.key.entry;
				iStategroup(this.stategroup).deselect();
				addStateAttributes(this.state);
				this.state.properties.is_selected.set("yes", {
					"all rules are valid": ["yes", {}],
				});
				const is_set = this.stategroup.properties.is_set.set("yes", {
					state: state_name,
					by: by,
				});
				switch (is_set.properties.by.state.name) {
					case "engine":
						break;
					case "user":
						setStickyStateGroupValue(this.stategroup.inferences.state_group_type(), state_name);
				}
				stop_updating(this.stategroup.parent);
			},
		});
		const modified = this.stategroup.properties.initialized.switch({
			no: () => "yes" as "no" | "yes",
			yes: ($) => ($.properties.state_in_backend.ref === this.state ? "no" : "yes"),
		});
		this.stategroup.properties.is_modified.ensure(modified, {});
		if (modified === "yes") {
			setModified(this.stategroup.properties.is_modified.cast("yes"));
		}
		notifyEntityUpdate(this.stategroup.parent);
	}

	select(by: "user" | "engine") {
		this.__select(by);
		this.graph?.moveToSink(this.entry!);
	}

	__server_state(): void {
		const state_name = this.state.key_value;
		this.state.properties.location.ensure("remote", {});
		setNodeLocationToRemote(this.state.properties.node);
		for (const other_state of this.stategroup.properties.states.values()) {
			if (other_state === this.state) continue;
			setStateLocationToLocal(other_state);
		}
		this.stategroup.properties.initialized.set("yes", {
			"state in backend": state_name,
		});
		switch (this.stategroup.properties.is_set.state.name) {
			case "yes": {
				const current_state = this.stategroup.properties.is_set.state.node.properties.state;
				switch (this.stategroup.properties.is_modified.state.name) {
					case "yes":
						if (current_state.entry === state_name) {
							this.__select("engine");
						}
						break;
					case "no":
						this.__select("engine");
						break;
				}
				break;
			}
			case "no":
				this.__select("engine");
				break;
		}
	}

	setServerState() {
		const old_backend_state = this.stategroup.properties.initialized.switch({
			no: () => undefined,
			yes: ($) => $.properties.state_in_backend.ref,
		});
		if (old_backend_state !== this.state) {
			if (this.is_selected.value !== Failed) {
				iNode(this.is_selected.value).discard();
			}
			this.__server_state();
			this.graph?.setServerEdgeValue(this.entry!, Sink);
		}
	}
}

export type iState = StateInterface;
export const iState = WeakCache<State, StateInterface>((s) => new StateInterface(s));
